import { FC, useEffect, useState } from "react";
import { useLocation } from "wouter";
import { AnimatePresence, motion } from "framer-motion";
import { Frown } from "lucide-react";
import { useAppSession } from "@/contexts/appSession";
import { useService } from "@/hooks/useService";
import { RPC_TIER_GLOBAL } from "@/constants/rpcPriorities";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { 
  INVITATION_SERVICE_IDENT,
  type IInvitationService,
  type UseInviteKeyErrorCodes,
} from "@bloomstack/apis/services/invitation";

type InviteLandingProps = {
  inviteKey: string;
};

type RedemptionState = 
  | { status: "redeeming" }
  | { status: "success"; workspaceId: string }
  | { status: "error"; code: UseInviteKeyErrorCodes | "CONNECTION_ERROR" | "UNKNOWN"; message: string };

/**
 * Invite landing page - handles invite key redemption flow.
 * 
 * Note: By the time this component renders, AppLoadingOverlay has already
 * confirmed the session is initialized (socket connected, user authenticated).
 * 
 * Flow:
 * 1. Call useInviteKey to validate and join the workspace
 * 2. On success: redirect to /t/workspace/{workspaceId}
 * 3. On error: show appropriate message
 */
export const InviteLanding: FC<InviteLandingProps> = ({ inviteKey }) => {
  const [, navigate] = useLocation();
  const { user } = useAppSession();
  const invitationService = useService<IInvitationService>(INVITATION_SERVICE_IDENT)
    .withPriority('normal', () => RPC_TIER_GLOBAL);
  
  const [state, setState] = useState<RedemptionState>({ status: "redeeming" });
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // Start redemption - session is already initialized by AppLoadingOverlay
    const redeemInvite = async () => {
      // Guard: userId should always exist after AppLoadingOverlay completes
      if (!user.userId) {
        setState({
          status: "error",
          code: "UNKNOWN",
          message: "Unable to verify your identity. Please try refreshing the page.",
        });
        return;
      }

      try {
        const result = await invitationService.withTimeout(15000).useInviteKey(inviteKey, user.userId);

        if (result.success) {
          setState({ status: "success", workspaceId: result.result.roomId });
          // Redirect to workspace after brief delay
          setTimeout(() => {
            navigate(`/t/workspace/${result.result.roomId}`);
          }, 500);
        } else {
          // Map error codes to user-friendly messages
          const errorMessages: Record<UseInviteKeyErrorCodes, string> = {
            NOT_FOUND: "This invite link is invalid or has been revoked.",
            EXPIRED: "This invite link has expired.",
            FORBIDDEN: "You don't have permission to use this invite.",
            DATABASE_ERROR: "Something went wrong. Please try again.",
            INTERNAL_ERROR: "Something went wrong. Please try again.",
            OWNER_USER_LIMIT: "This workspace has reached its member limit.",
          };

          setState({
            status: "error",
            code: result.error.code,
            message: errorMessages[result.error.code] || "Something went wrong.",
          });
        }
      } catch (err) {
        console.error("Invite redemption error:", err);
        setState({
          status: "error",
          code: "CONNECTION_ERROR",
          message: "Unable to connect to the server. Please check your connection and try again.",
        });
      }
    };

    redeemInvite();
  }, [inviteKey, user.userId, invitationService, navigate, retryCount]);

  // Retry handler
  const handleRetry = () => {
    setState({ status: "redeeming" });
    setRetryCount(c => c + 1);
  };

  // Render based on state
  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-zinc-50 dark:bg-zinc-900">
      <div className="text-center max-w-md px-6">
        <AnimatePresence mode="popLayout">
          {state.status === "redeeming" && (
            <motion.div
              key="redeeming"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex justify-center">
                <Spinner size="48px" />
              </div>
              <h1 className="text-2xl font-bold mt-4 mb-2">Joining Workspace</h1>
              <p className="text-zinc-600 dark:text-zinc-400">
                Processing your invite...
              </p>
            </motion.div>
          )}

          {state.status === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="text-green-500 text-5xl mb-4">✓</div>
              <h1 className="text-2xl font-bold mb-2">You're In!</h1>
              <p className="text-zinc-600 dark:text-zinc-400">
                Redirecting to workspace...
              </p>
            </motion.div>
          )}

          {state.status === "error" && (
            <motion.div
              key="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex justify-center mb-4">
                <motion.div
                  whileHover={{
                    rotate: [0, -10, 10, -10, 10, 0],
                    transition: { duration: 0.5 }
                  }}
                  className="cursor-pointer"
                >
                  <Frown className="w-12 h-12 text-zinc-500 dark:text-zinc-400" />
                </motion.div>
              </div>
              <h1 className="text-2xl font-bold mb-2">Unable to Join</h1>
              <p className="text-zinc-600 dark:text-zinc-400 mb-6">
                {state.message}
              </p>
              <div className="flex gap-3 justify-center">
                <Button
                  intent="none"
                  onClick={() => navigate("/")}
                  label="Go Home"
                />
                <Button
                  intent="primary"
                  onClick={handleRetry}
                  label="Try Again"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
